import { motion } from 'motion/react'
import { type Ref, useCallback, useEffect, useRef, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'

export const ITEM_HEIGHT_CACHE: Record<string, number> = {}

interface VirtualizedListItemProps {
	children: React.ReactNode
	id: string
	ref?: Ref<HTMLDivElement>
}

function VirtualizedListItem({ children, id, ref }: VirtualizedListItemProps) {
	const rootRef = useRef<HTMLDivElement>(null)
	const mergedRefs = mergeRefs([ref, rootRef])
	const [isVisible, setIsVisible] = useState(true)
	const [height, setHeight] = useState<number | undefined>(ITEM_HEIGHT_CACHE[id])

	// 计算并缓存元素高度
	const cacheHeight = useCallback(
		(element: HTMLElement) => {
			const newHeight = element.getBoundingClientRect().height
			setHeight(newHeight)
			ITEM_HEIGHT_CACHE[id] = newHeight
		},
		[id]
	)

	// 监听元素可见性
	useEffect(() => {
		const root = rootRef.current
		if (!root) return

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						cacheHeight(entry.target as HTMLElement)
						setIsVisible(false)
					} else {
						setIsVisible(true)
					}
				})
			},
			{
				rootMargin: '50px 0px',
			}
		)

		observer.observe(root)
		return () => observer.disconnect()
	}, [cacheHeight])

	return (
		<motion.div
			ref={mergedRefs}
			style={
				!isVisible
					? {
							height,
						}
					: undefined
			}
			animate={{
				opacity: isVisible ? 1 : 0,
			}}
			transition={{
				type: 'spring',
				damping: 20,
			}}
		>
			{isVisible ? children : null}
		</motion.div>
	)
}

export default VirtualizedListItem
